import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from 'components/ui/Modal'
import { Button } from 'components/ui/Button'

interface InterviewOnboardingModalProps {
  isOpen: boolean
  onComplete: () => void
}

const DEMO_STEP_FILES = [
  {
    file: '01-welcome.png',
    titleKey: 'interview.page.guideStep1Title',
  },
  {
    file: '02-device-test.png',
    titleKey: 'interview.page.guideStep2Title',
  },
  {
    file: '03-device-test-active.png',
    titleKey: 'interview.page.guideStep3Title',
  },
  {
    file: '04-answer-question.png',
    titleKey: 'interview.page.guideStep4Title',
  },
  {
    file: '05-finish.png',
    titleKey: 'interview.page.guideStep5Title',
  },
] as const

function getDemoImageBasePath(language: string) {
  const lang = language.split('-')[0].toLowerCase()
  return lang === 'en' ? '/interview-demo/en' : '/interview-demo'
}

export function InterviewOnboardingModal({ isOpen, onComplete }: InterviewOnboardingModalProps) {
  const { t, i18n } = useTranslation()
  const [step, setStep] = useState(0)

  const demoSteps = useMemo(
    () =>
      DEMO_STEP_FILES.map((item) => ({
        ...item,
        image: `${getDemoImageBasePath(i18n.language)}/${item.file}`,
      })),
    [i18n.language]
  )

  useEffect(() => {
    if (isOpen) setStep(0)
  }, [isOpen, i18n.language])

  const totalSteps = demoSteps.length
  const isLast = step === totalSteps - 1
  const current = demoSteps[step]

  const handleNext = () => {
    if (isLast) {
      onComplete()
      return
    }
    setStep((s) => s + 1)
  }

  const handleBack = () => {
    if (step > 0) setStep((s) => s - 1)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onComplete}
      title={t('interview.page.guideTitle')}
      size="xl"
      hideCloseButton={false}
      footer={
        <div className="flex items-center justify-between gap-3 w-full">
          <div className="flex items-center gap-1.5">
            {demoSteps.map((_, i) => (
              <span
                key={i}
                className={`h-2 rounded-full transition-all ${
                  i === step ? 'w-6 bg-primary' : i < step ? 'w-2 bg-primary/50' : 'w-2 bg-muted'
                }`}
              />
            ))}
            <span className="ml-2 text-xs text-muted-foreground">
              {step + 1}/{totalSteps}
            </span>
          </div>
          <div className="flex gap-2">
            {step > 0 && (
              <Button variant="outline" onClick={handleBack}>
                {t('interview.page.guideBack')}
              </Button>
            )}
            <Button onClick={handleNext}>
              {isLast ? t('interview.page.guideGotIt') : t('interview.page.guideNext')}
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">{t(current.titleKey)}</p>
        <div className="rounded-lg border bg-muted/30 overflow-hidden">
          <img
            key={current.image}
            src={current.image}
            alt={t(current.titleKey)}
            className="w-full h-auto max-h-[60vh] object-contain bg-background"
            draggable={false}
          />
        </div>
      </div>
    </Modal>
  )
}
